import React from 'react';
import ProductSuggestions from './ProductSuggestions';
import ScanButton from './ScanButton';
import { teachBarcode } from '../../api/animals';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { upsertAnimalSupply, type CareSupply, type SupplyUnit } from '../../api/animals';
import { describeSupply, formatQuantity, FOOD_UNITS, LITTER_UNITS } from '../../utils/supplies';
import { Link } from 'react-router-dom';
import { CalcoDespensa } from '../icons/calcados';

type Kind = 'food' | 'litter';

type Props = {
  animalId: string;
  foods: CareSupply[];
  litters: CareSupply[];
  showLitter: boolean;
  onChange: () => void;
  /** Para que las sugerencias del catálogo sean de su especie. */
  species?: string;
};

const MUTED = '#7A8273';
const WARN = '#8F3827';

/**
 * Existencias de la despensa: cuánto queda de cada producto y para cuántos usos.
 *
 * Vive debajo de los botones de cuidado y no en una pantalla aparte porque la
 * pregunta ("¿queda pienso?") aparece justo cuando se va a dar de comer.
 */
export default function SupplyList({ animalId, foods, litters, showLitter, onChange, species }: Props) {
  const [editing, setEditing] = React.useState<null | { kind: Kind; supply?: CareSupply }>(null);

  const save = useMutation({
    mutationFn: (payload: Parameters<typeof upsertAnimalSupply>[1]) => upsertAnimalSupply(animalId, payload),
    onSuccess: () => {
      setEditing(null);
      onChange();
    },
    onError: (error: any) => {
      const code = error?.response?.data?.error;
      if (code === 'unit_mismatch') return toast.error('El paquete y la ración tienen que ir en la misma magnitud.');
      if (code === 'name_required') return toast.error('Ponle nombre al producto.');
      if (code === 'name_taken') return toast.error('Ya tienes un producto con ese nombre.');
      if (code === 'pack_size_required') return toast.error('Para reponer hace falta saber cuánto trae el paquete.');
      toast.error('No se pudo guardar el producto');
    },
  });

  const rows: Array<{ kind: Kind; supply: CareSupply }> = [
    ...foods.map(supply => ({ kind: 'food' as Kind, supply })),
    ...(showLitter ? litters.map(supply => ({ kind: 'litter' as Kind, supply })) : []),
  ];

  const tracked = rows.filter(r => r.supply.usesLeft !== null);

  return (
    <div style={{ marginTop: 9, padding: '0 7px 0 5px', display: 'grid', gridTemplateColumns: '26px minmax(0, 1fr)', columnGap: 10, minWidth: 0 }}>
      <CalcoDespensa size={21} style={{ marginTop: 1 }} />
      <div style={{ minWidth: 0 }}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 style={{ margin: 0, fontSize: 14.5, lineHeight: '18px', fontWeight: 700, color: '#1E2A27' }}>Despensa</h3>
        <button
          type="button"
          onClick={() => setEditing({ kind: 'food' })}
          style={{ color: '#1F6F6F', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, lineHeight: '18px', fontWeight: 700 }}
        >
          + Añadir producto
        </button>
      </div>

      {tracked.length === 0 ? (
        <p style={{ color: '#4A504C', marginTop: 2, fontSize: 12, lineHeight: '15px' }}>
          {rows.length === 0
            // A un perro no se le nombra la arena. `showLitter` ya decidía qué
            // filas y qué botones se pintan (`usesLitter(species)`), pero este
            // texto se escribió pensando solo en el gato y le ofrecía a la
            // familia de un perro apuntar algo que no compra.
            ? (showLitter
                ? 'Apunta el pienso y la arena que usas y te diremos para cuántas comidas queda.'
                : 'Apunta el pienso que usas y te diremos para cuántas comidas queda.')
            : 'Di cuánto trae el paquete y qué ración le pones, y te diremos para cuántas comidas queda.'}
        </p>
      ) : null}

      <div className="grid gap-2" style={{ marginTop: 8 }}>
        {rows.map(({ kind, supply }) => {
          const detail = describeSupply(supply, kind === 'food' ? 'comidas' : 'cambios');
          return (
            <div key={`${kind}-${supply.name}`} className="flex flex-wrap items-center justify-between gap-2">
              <div style={{ minWidth: 0 }}>
                <div className="text-sm">
                  {supply.name}
                  {supply.remaining !== undefined && (
                    <span style={{ color: MUTED }}> · queda {formatQuantity(supply.remaining, supply.unit)}</span>
                  )}
                </div>
                <div className="text-xs" style={{ color: supply.runningLow ? WARN : MUTED }}>
                  {detail ? (supply.runningLow ? `⚠️ Se está acabando: ${detail}` : `Para ${detail}`) : 'Sin cuenta de existencias'}
                </div>
              </div>
              <div className="flex gap-2">
                {!!supply.packSize && (
                  <button
                    type="button"
                    onClick={() => save.mutate({ kind, name: supply.name, refill: true })}
                    disabled={save.isPending}
                    className="text-xs font-semibold"
                    style={linkBtn}
                  >
                    Reponer
                  </button>
                )}
                {supply.runningLow && (
                  // El enlace aparece justo cuando hace falta comprar, que es la
                  // única razón por la que alguien mira esta lista.
                  <Link
                    to={`/comprar?producto=${encodeURIComponent(supply.name)}`}
                    className="text-xs font-semibold"
                    style={{ color: '#1F6F6F' }}
                  >
                    Dónde comprarlo
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => setEditing({ kind, supply })}
                  className="text-xs font-semibold"
                  style={linkBtn}
                >
                  Editar
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <SupplyForm
          kind={editing.kind}
          // El mismo criterio que las filas: sin esto, "+ Añadir producto"
          // enseñaba el par Comida/Arena también en la ficha de un perro.
          showLitter={showLitter}
          species={species}
          supply={editing.supply}
          busy={save.isPending}
          onCancel={() => setEditing(null)}
          onDelete={
            editing.supply
              ? () => save.mutate({ kind: editing.kind, name: editing.supply!.name, remove: true })
              : undefined
          }
          onSave={payload => save.mutate(payload)}
        />
      )}
      </div>
    </div>
  );
}

function SupplyForm({
  kind: initialKind,
  showLitter,
  species,
  supply,
  busy,
  onCancel,
  onDelete,
  onSave,
}: {
  kind: Kind;
  showLitter: boolean;
  species?: string;
  supply?: CareSupply;
  busy?: boolean;
  onCancel: () => void;
  onDelete?: () => void;
  onSave: (payload: Parameters<typeof upsertAnimalSupply>[1]) => void;
}) {
  const [kind, setKind] = React.useState<Kind>(initialKind);
  const [name, setName] = React.useState(supply?.name || '');
  // Código escaneado sin nombre conocido: se enseña al guardar con el nombre escrito.
  const [pendingCode, setPendingCode] = React.useState<string | null>(null);
  // El formulario habla en la unidad grande, que es la de la etiqueta del saco;
  // la ración se escribe en la pequeña, que es la de la báscula de cocina.
  const units = kind === 'food' ? FOOD_UNITS : LITTER_UNITS;
  const [packUnit, setPackUnit] = React.useState<SupplyUnit>(kind === 'food' ? 'kg' : 'l');
  const [perUseUnit, setPerUseUnit] = React.useState<SupplyUnit>(kind === 'food' ? 'g' : 'l');
  const [packSize, setPackSize] = React.useState(
    supply?.packSize ? String(supply.packSize / (packUnitFactor(kind === 'food' ? 'kg' : 'l'))).replace('.', ',') : '',
  );
  const [perUse, setPerUse] = React.useState(
    supply?.perUse ? String(supply.perUse / packUnitFactor(kind === 'food' ? 'g' : 'l')).replace('.', ',') : '',
  );

  const num = (value: string) => {
    const parsed = Number(value.replace(',', '.'));
    return value.trim() && Number.isFinite(parsed) ? parsed : undefined;
  };

  return (
    <div style={backdrop} onClick={() => !busy && onCancel()}>
      <div role="dialog" aria-modal="true" style={modal} onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold">{supply ? 'Editar producto' : 'Añadir producto'}</h3>

        {!supply && showLitter && (
          <div className="flex gap-2" style={{ marginTop: 10 }}>
            {(['food', 'litter'] as Kind[]).map(option => (
              <button
                key={option}
                type="button"
                onClick={() => setKind(option)}
                aria-pressed={kind === option}
                className="border px-3 py-2 text-sm"
                style={{
                  borderRadius: 999,
                  borderColor: kind === option ? '#1F6F6F' : '#D7D0C2',
                  background: kind === option ? '#1F6F6F' : '#FFFFFF',
                  color: kind === option ? '#FFFFFF' : '#3F4A3C',
                  cursor: 'pointer',
                }}
              >
                {option === 'food' ? 'Comida' : 'Arena'}
              </button>
            ))}
          </div>
        )}

        <label className="text-sm" style={{ display: 'block', marginTop: 10 }}>
          <span style={{ color: MUTED }}>Producto</span>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={kind === 'food' ? 'Acana Adult' : 'Aglomerante'}
            className="w-full border px-3 py-2 text-sm"
            style={{ borderRadius: 8, borderColor: '#D7D0C2', marginTop: 4 }}
          />
        </label>
        {!supply && (
          <div className="flex items-center gap-2" style={{ marginTop: 8 }}>
            <ScanButton
              onResult={r => {
                if (r.name) { setName(r.name); setPendingCode(null); }
                else setPendingCode(r.code);
              }}
            />
            {pendingCode && (
              <span className="text-xs" style={{ color: '#8F3827' }}>No conocemos ese producto: escribe su nombre.</span>
            )}
          </div>
        )}
        {!supply && name.trim().length >= 2 && (
          <ProductSuggestions q={name} kind={kind} species={species} onPick={setName} />
        )}

        <AmountField
          label={kind === 'food' ? 'Trae el paquete' : 'Trae el saco'}
          value={packSize}
          onChange={setPackSize}
          unit={packUnit}
          onUnit={setPackUnit}
          units={units}
          placeholder={kind === 'food' ? '6' : '10'}
        />
        <AmountField
          label={kind === 'food' ? 'Ración de cada comida' : 'Gasto por cambio'}
          value={perUse}
          onChange={setPerUse}
          unit={perUseUnit}
          onUnit={setPerUseUnit}
          units={units}
          placeholder={kind === 'food' ? '80' : '4'}
        />

        {kind === 'food' && (
          <p className="text-xs" style={{ color: MUTED, marginTop: 4 }}>
            Lo que le pones cada vez, no lo de todo el día. Se descuenta al marcar la comida.
          </p>
        )}

        <p className="text-xs" style={{ color: MUTED, marginTop: 8 }}>
          Con estos dos datos sabemos para cuántas {kind === 'food' ? 'comidas' : 'cambios'} queda. Puedes dejarlos en
          blanco: el producto seguirá estando para marcarlo de un toque.
        </p>

        <div className="flex flex-wrap justify-between gap-2" style={{ marginTop: 16 }}>
          <div>
            {!!onDelete && (
              <button type="button" onClick={onDelete} disabled={busy} style={{ ...btnGhost, color: WARN, borderColor: '#E3C4C0' }}>
                Quitar
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onCancel} disabled={busy} style={btnGhost}>Cancelar</button>
            <button
              type="button"
              disabled={busy || !name.trim()}
              onClick={() => {
                if (pendingCode) void teachBarcode(pendingCode, name.trim(), kind, species).catch(() => undefined);
                const renamed = supply && name.trim() !== supply.name;
                onSave({
                  kind,
                  name: supply ? supply.name : name.trim(),
                  ...(renamed ? { newName: name.trim() } : {}),
                  packSize: num(packSize),
                  packUnit,
                  perUse: num(perUse),
                  perUseUnit,
                });
              }}
              style={{ ...btnPrimary, opacity: busy || !name.trim() ? 0.5 : 1 }}
            >
              {busy ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AmountField({
  label, value, onChange, unit, onUnit, units, placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  unit: SupplyUnit;
  onUnit: (u: SupplyUnit) => void;
  units: SupplyUnit[];
  placeholder?: string;
}) {
  return (
    <label className="text-sm" style={{ display: 'block', marginTop: 10 }}>
      <span style={{ color: MUTED }}>{label}</span>
      <div className="flex gap-2" style={{ marginTop: 4 }}>
        <input
          inputMode="decimal"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="border px-3 py-2 text-sm"
          style={{ borderRadius: 8, borderColor: '#D7D0C2', flex: 1, minWidth: 0 }}
        />
        <select
          value={unit}
          onChange={e => onUnit(e.target.value as SupplyUnit)}
          className="border px-2 py-2 text-sm"
          style={{ borderRadius: 8, borderColor: '#D7D0C2' }}
        >
          {units.map(u => (
            <option key={u} value={u}>{u}</option>
          ))}
        </select>
      </div>
    </label>
  );
}

/** Factor de la unidad respecto a su base, para poder editar lo ya guardado. */
function packUnitFactor(unit: SupplyUnit): number {
  return unit === 'kg' || unit === 'l' ? 1000 : 1;
}

const btnPrimary: React.CSSProperties = {
  background: '#1F6F6F', color: '#FFFFFF', border: 'none', borderRadius: 10,
  padding: '10px 14px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
};

const btnGhost: React.CSSProperties = {
  background: '#FFFFFF', color: '#3F4A3C', border: '1px solid #D7D0C2', borderRadius: 10,
  padding: '10px 14px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
};

const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: '#1F6F6F', cursor: 'pointer', padding: 0,
};

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 2000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};

const modal: React.CSSProperties = {
  background: '#fff', borderRadius: 12, padding: 20, width: 'min(460px, 96vw)',
  maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 45px rgba(15,23,42,0.25)',
};
